import { QueueFlagsContext } from '@/components/QueueFlags/QueueFlagsContext';
import { useGetDocumentsQuery } from '@/services/documents';
import { NamesOfQueuesInsidePackages } from '@/types/Queues';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  FormikSelectInputNative,
  Modal,
  toast,
} from '@palmetto/palmetto-components';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContext, useMemo } from 'react';
import * as yup from 'yup';

import {
  AllDocumentTypes,
  documentTitles,
  Flag,
  FlagPurposeToQueueStatusLabelMap,
  FlagPurposeType,
  FlagTypeLabels,
  InstallDocumentTypes,
  ProgramType,
  QueueHoldReason,
  QueueStatus,
  queueStatusMap,
  QueueType,
} from 'types';

import { useQueueClaimConflict } from '../../../hooks/useQueueClaimConflict';
import { useUpdateQueueMutation } from '../../../services/queues';
import { useGetCurrentDesignQuery } from '../../../services/systemDesign';

interface CompleteQueueReviewModalFactoryProps {
  handleClose: () => void;
  handleFormSave: (targetStatus?: QueueStatus) => Promise<void>;
  disableApproval?: boolean;
  conditionallyApprovedFlags?: Flag[];
  subQueueInfo?: {
    type: QueueType | undefined;
    status: QueueStatus | undefined;
  };
}

interface ReviewFormValues {
  status: QueueStatus;
  note?: string;
  exceptionNote?: string;
  holdReason?: string;
}

const queueUpdateSchema = yup.object({
  notes: yup.string().optional().nullable(),
  exceptionNote: yup.string().optional().nullable(),
  status: yup.string().required(),
});

const getFlagListColor = (flagPurpose?: FlagPurposeType) => {
  switch (flagPurpose) {
    case FlagPurposeType.additionalReview:
      return 'grey-100';
    case FlagPurposeType.stipulation:
      return 'warning-100';
    case FlagPurposeType.changeOrder:
      return 'info-100';
    default: // rejected or no purpose type
      return 'danger-100';
  }
};

const getReviewColor = (status?: string) => {
  switch (status) {
    case QueueStatus.approved:
      return 'success';
    case QueueStatus.rejected:
      return 'danger';
    case QueueStatus.onHold:
      return 'default';
    case QueueStatus.conditionallyApproved:
      return 'warning';
    default:
      return 'default';
  }
};

const programTypesThatRequireArrayDataValidation = new Set([
  ProgramType.solar,
  ProgramType.newHomes,
  ProgramType.doePr,
]);

export const CompleteQueueRevampReviewModal = ({
  handleClose,
  handleFormSave,
  conditionallyApprovedFlags,
  subQueueInfo,
  disableApproval,
}: CompleteQueueReviewModalFactoryProps) => {
  const { getAllFlags, queue } = useContext(QueueFlagsContext);
  const [updateQueue] = useUpdateQueueMutation();
  const flags = getAllFlags(false);
  const hasFlags = conditionallyApprovedFlags
    ? flags?.length + conditionallyApprovedFlags.length > 0
    : flags?.length > 0;
  const { installPhotosForHvac } = useFlags();
  const { data: installDocumentationDocs = [] } = useGetDocumentsQuery({
    accountId: queue?.account.id ? queue.account.id.toString() : '',
    documentTypeFilter: [InstallDocumentTypes.hvacInstallationPhotos],
  });

  const hasPendingInstallPhotosForHvac = installPhotosForHvac && installDocumentationDocs?.length > 0;

  const isSubQueueRejected = subQueueInfo?.status === QueueStatus.rejected;

  const isInstallPackageQueue = queue?.type === QueueType.installPackage;
  const programType = queue?.account?.programType;
  const shouldSkipArrayValidation = programType
    ? !programTypesThatRequireArrayDataValidation.has(programType)
    : false;

  const {
    data: systemDesign,
    isLoading: isSystemDesignLoading,
    isError: isSystemDesignError,
  } = useGetCurrentDesignQuery(
    {
      id: queue?.account?.id?.toString() || '',
      programType: programType || ProgramType.solar,
    },
    {
      skip: !isInstallPackageQueue || !queue?.account?.id || shouldSkipArrayValidation,
    },
  );

  const hasArrayData = useMemo(() => {
    if (!isInstallPackageQueue) return true;
    if (shouldSkipArrayValidation) return true;
    if (isSystemDesignLoading || isSystemDesignError) return true;
    return systemDesign?.arrays && systemDesign.arrays.length > 0;
  }, [
    isInstallPackageQueue,
    shouldSkipArrayValidation,
    isSystemDesignLoading,
    isSystemDesignError,
    systemDesign?.arrays,
  ]);

  const reviewStatus = useMemo(() => {
    if (disableApproval) {
      return QueueStatus.rejected;
    }
    if (
      (!flags || !flags.length) &&
      (!conditionallyApprovedFlags || !conditionallyApprovedFlags?.length) &&
      !isSubQueueRejected
    ) {
      return QueueStatus.approved;
    }
    if (flags?.some((flag) => flag.purposeType === FlagPurposeType.additionalReview)) {
      return QueueStatus.onHold;
    }
    if (
      flags?.some((flag) => flag.purposeType === FlagPurposeType.rejection) ||
      flags.every((flag) => flag.purposeType === undefined) ||
      (conditionallyApprovedFlags && conditionallyApprovedFlags.length > 0) ||
      isSubQueueRejected
    ) {
      return QueueStatus.rejected;
    }
    if (flags?.some((flag) => flag.purposeType === FlagPurposeType.stipulation)) {
      return QueueStatus.conditionallyApproved;
    }
    return QueueStatus.approved;
  }, [conditionallyApprovedFlags, disableApproval, flags, isSubQueueRejected]);

  const queueOnHoldReason = useMemo(() => {
    return queue &&
      queue.holdReasons &&
      queue.holdReasons[0] &&
      queue.holdReasons[0].reasons &&
      queue.holdReasons[0].reasons[0]
      ? queue.holdReasons[0].reasons[0]
      : '';
  }, [queue]);

  const { checkClaimAndProceed, claimConflictModal } = useQueueClaimConflict(queue);

  const performSubmit = async (values: ReviewFormValues, { setSubmitting }: FormikHelpers<ReviewFormValues>) => {
    try {
      const data = {
        status: values.status,
        note: values?.exceptionNote ?? values?.note ?? '',
        holdReasons: values?.holdReason ? [{ reasons: [values.holdReason], note: '' }] : undefined,
      };
      let formSaveSuccess = true;
      try {
        await handleFormSave(values.status);
      } catch (e: any) {
        formSaveSuccess = false;
        toast.error(e?.message || 'Error saving the review.');
      }

      if (!formSaveSuccess) {
        setSubmitting(false);
        return;
      }

      await updateQueue({
        id: queue?.id,
        data,
      }).unwrap();
      toast.success('Review completed.');
      handleClose();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Error completing the review.');
    }
  };

  const handleSubmit = (...args: Parameters<typeof performSubmit>) => {
    checkClaimAndProceed(() => performSubmit(...args));
  };

  const flagsByPurposeType = useMemo(() => {
    const allFlags = getAllFlags ? getAllFlags(true) : [];
    const flagCategories = [
      FlagPurposeType.rejection,
      FlagPurposeType.stipulation,
      FlagPurposeType.additionalReview,
    ];

    return flagCategories.reduce(
      (acc, purpose) => {
        const filteredFlags = allFlags?.filter(
          (flag: Flag) =>
            ((!flag.purposeType || !flag.flagType) && purpose === FlagPurposeType.rejection && !flag.resolved) ||
            (flag.purposeType === purpose && flag.flagType && !flag.resolved),
        );

        const mergedFlags =
          conditionallyApprovedFlags &&
          conditionallyApprovedFlags.length > 0 &&
          purpose === FlagPurposeType.stipulation
            ? [...filteredFlags, ...(conditionallyApprovedFlags || [])]
            : filteredFlags;

        acc[purpose] =
          mergedFlags?.toSorted((a, b) => {
            const aResolved = a.resolved === true ? 1 : 0;
            const bResolved = b.resolved === true ? 1 : 0;
            return aResolved - bResolved;
          }) || [];

        return acc;
      },
      {} as Record<string, Flag[]>,
    );
  }, [conditionallyApprovedFlags, getAllFlags]);

  const renderFlagList = (flagPurpose: FlagPurposeType) => {
    const count = flagsByPurposeType[flagPurpose]?.length || 0;
    const FlagLabel = FlagPurposeToQueueStatusLabelMap[flagPurpose] || 'Unknown Flag Type';

    return count > 0 ? (
      <>
        <Box
          as="h3"
          fontSize="lg"
          fontWeight="bold"
          direction="row"
          background={getFlagListColor(flagPurpose)}
          color="grey-700"
          radius="md md 0 0"
          padding="sm"
        >
          {FlagLabel} &#8729;&nbsp;
          <Box color="body-tertiary">{count}</Box>
        </Box>
        <Box background="primary">
          <Box>
            {flagsByPurposeType[flagPurpose].map((flag, index) => {
              const flagProblemsAsText = flag?.problems?.map(
                (problem, i) =>
                  `${problem.displayText}${flag?.problems && i !== flag?.problems?.length - 1 ? '; ' : ''}`,
              );
              const typeLabelKey = flag?.flagType || flag?.type;
              const typeLabel = typeLabelKey
                ? `${FlagTypeLabels[typeLabelKey as keyof typeof FlagTypeLabels] || documentTitles[typeLabelKey as AllDocumentTypes]}:`
                : `${flag?.category || flag?.dataKey}:`;
              return (
                <Box
                  key={index}
                  direction="row"
                  justifyContent="space-between"
                  borderColor="separator"
                  borderWidth={`0 0 ${index !== count - 1 ? 'xs' : '0'} 0`}
                  alignItems="center"
                >
                  <Box fontSize="md" padding="md" direction="row" childGap="sm">
                    <Box direction="row" childGap="sm">
                      <Box fontWeight="bold" as="span">
                        {typeLabel}
                      </Box>
                      <Box fontWeight="light" as="span">
                        {flag?.reason ? flag.reason : flagProblemsAsText}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </>
    ) : null;
  };

  const handleValidation = (values: any) => {
    const errors = {} as any;
    if (values.status === QueueStatus.onHold && !values.holdReason) {
      errors.holdReason = 'Hold Reason is required';
    }
    return errors;
  };

  return (
    <Formik<ReviewFormValues>
      initialValues={{ status: reviewStatus, holdReason: queueOnHoldReason }}
      onSubmit={handleSubmit}
      enableReinitialize={true}
      validate={handleValidation}
      validationSchema={queueUpdateSchema}
      validateOnChange={false}
    >
      {({ isSubmitting, values }) => (
        <Form noValidate id="completeQueueReview">
          <Modal.Body background="secondary" childGap="lg" fontSize="sm">
            <Box direction="row" gap="sm">
              <Box as="p" fontWeight="bold" color="body-primary">
                Review Status:
              </Box>
              <Badge message={queueStatusMap[values?.status]} variant={getReviewColor(values?.status)} size="lg" />
            </Box>
            {isSubQueueRejected && (
              <Alert
                variant="info"
                message={
                  <>
                    The review status is rejected because{' '}
                    <strong>
                      {subQueueInfo?.type
                        ? NamesOfQueuesInsidePackages[subQueueInfo.type]
                        : 'an internal queue of this package'}
                    </strong>{' '}
                    is in rejected status.
                  </>
                }
              />
            )}
            {!hasArrayData && (
              <Alert
                variant="warning"
                message="Reminder: Array Data should be added to PV System Details prior to Approving Install Milestone."
              />
            )}
            {hasFlags && (
              <Card>
                <Card.Section padding="sm">
                  {renderFlagList(FlagPurposeType.rejection)}
                  {renderFlagList(FlagPurposeType.stipulation)}
                  {renderFlagList(FlagPurposeType.additionalReview)}
                </Card.Section>
              </Card>
            )}
            {values?.status === QueueStatus.onHold && (
              <Box>
                <Field
                  label="Why are you putting this package on hold?"
                  name="holdReason"
                  id="holdReason"
                  component={FormikSelectInputNative}
                  options={Object.values(QueueHoldReason).map((reason) => ({
                    label: reason,
                    value: reason,
                  }))}
                  isDisabled={isSubmitting}
                />
              </Box>
            )}
            <Box childGap="sm">
              {hasPendingInstallPhotosForHvac && (
                <Alert
                  variant="warning"
                  title="EPC Attachments in All Installation Documentation's Section"
                  message="Any files still in the Uploaded Install Documentation section will not be visible to the Installer. Please ensure all files pertinent to your review have been moved to the appropriate section."
                />
              )}
              <Box
                as="p"
                style={{
                  marginTop: '0.4em',
                }}
              >
                Any fields updated during your review will be saved when Completing Review or Placing On Hold
              </Box>
            </Box>
          </Modal.Body>
          <Modal.Footer justifyContent="flex-end">
            <Box direction="row" gap="sm">
              <Button variant="secondary" tone="neutral" size="md" onClick={handleClose} isDisabled={isSubmitting}>
                Cancel
              </Button>
              {values.status === QueueStatus.onHold ? (
                <Button
                  size="md"
                  variant="primary"
                  tone="neutral"
                  iconPrefix="pause"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Place on Hold
                </Button>
              ) : (
                <Button size="md" variant="primary" type="submit" isLoading={isSubmitting}>
                  Complete Review
                </Button>
              )}
            </Box>
          </Modal.Footer>
          {claimConflictModal}
        </Form>
      )}
    </Formik>
  );
};
